import AreaManage from "../img/AreaManage.png"
import algoplan from "../img/algoplan.png"
import OrderManagement from "../img/OrderManagement.jpeg"
import flexleasing from "../img/flexleasing.png"
import starc from "../img/starc.png"
import mounthealing from "../img/mounthealing.png"

export const projectsData = [
    {
        id: 1,
        title: "Area Management System",
        tagline: "Real-time spatial data orchestration platform",
        category: "Full-Stack",
        badge: "Geospatial",
        shortDescription: "Advanced spatial data management system enabling real-time hierarchical container, layer, and area operations.",
        fullDescription: `
            Architected and developed a comprehensive Area Management System that revolutionizes how organizations handle spatial data. 
            The platform features a sophisticated real-time data handling layer utilizing Angular Signals and RxJS Subjects for optimized state management.
            
            Key Achievements:
            • Implemented hierarchical data structures (Containers > Layers > Areas) with real-time synchronization
            • Engineered responsive Angular UI with advanced signal-based state management reducing re-renders by 60%
            • Designed scalable .NET 7 microservices APIs handling 10,000+ concurrent operations
            • Integrated MongoDB for flexible document storage with sub-millisecond query optimization
            • Deployed on Azure with auto-scaling capabilities supporting 99.99% uptime SLA
        `,
        highlights: [
            { icon: "⚡", text: "60% reduction in UI re-renders using Angular Signals" },
            { icon: "📊", text: "Real-time data sync across 1000+ connected clients" },
            { icon: "🔐", text: "Enterprise-grade security with role-based access control" },
            { icon: "📈", text: "Handling 10,000+ concurrent operations seamlessly" }
        ],
        images: [AreaManage],
        techStack: [
            { name: "Angular v17", category: "Frontend" },
            { name: "TypeScript", category: "Frontend" },
            { name: "Leaflet", category: "Frontend" },
            { name: "MapBox", category: "Frontend" },
            { name: "RxJS", category: "Frontend" },
            { name: "Angular Signals", category: "Frontend" },
            { name: ".NET 7", category: "Backend" },
            { name: "Clean Architecture", category: "Backend" },
            { name: "Entity Framework Core", category: "Backend" },
            { name: "MongoDB", category: "Database" },
            { name: "Azure", category: "Cloud" },
            { name: "Docker", category: "Cloud" }
        ],
        year: "2025",
        duration: "6 months",
        team: "5-person engineering team",
        impact: "Reduced data processing time by 70% and improved user satisfaction to 9.2/10"
    },
    {
        id: 2,
        title: "Algo Planner",
        tagline: "Performance-optimized algorithmic planning dashboard",
        category: "Full-Stack",
        badge: "Analytics",
        shortDescription: "Enterprise-grade dashboard delivering real-time algorithmic planning with optimized rendering and API integration.",
        fullDescription: `
            Architected a high-performance analytics dashboard for algorithmic planning that serves as the nerve center for enterprise operations.
            The system integrates complex data pipelines with intuitive UI, delivering actionable insights in real-time.
            
            Key Achievements:
            • Redesigned dashboard architecture reducing initial load time from 8.2s to 1.8s (78% improvement)
            • Optimized data-loading efficiency through intelligent caching and pagination strategies
            • Integrated 15+ APIs with circuit-breaker pattern for resilience
            • Implemented performant charting and visualizations handling 100K+ data points
            • Achieved 98% test coverage with comprehensive unit and integration tests
        `,
        highlights: [
            { icon: "🚀", text: "78% faster load time through strategic optimization" },
            { icon: "📡", text: "Real-time API integration with 15+ data sources" },
            { icon: "📊", text: "Advanced charting with 100K+ data points handling" },
            { icon: "✅", text: "98% test coverage with comprehensive testing" }
        ],
        images: [algoplan],
        techStack: [
            { name: "Angular", category: "Frontend" },
            { name: "TypeScript", category: "Frontend" },
            { name: "DevExtreme", category: "Frontend" },
            { name: "RxJS", category: "Frontend" },
            { name: "Chart.js", category: "Frontend" },
            { name: ".NET Core", category: "Backend" },
            { name: "WebAPI", category: "Backend" },
            { name: "SQL Server", category: "Database" },
            { name: "Redis", category: "Cache" },
            { name: "Azure DevOps", category: "Cloud" },
            { name: "CI/CD", category: "Cloud" }
        ],
        year: "2024-2025",
        duration: "10 months",
        team: "4-person cross-functional team",
        impact: "Increased operational efficiency by 45% and enabled 200% more concurrent users"
    },
    {
        id: 3,
        title: "Order Management System (OMS)",
        tagline: "Enterprise-scale microservices order orchestration",
        category: "Backend",
        badge: "Enterprise",
        shortDescription: "Highly available microservices-based order management platform processing 50,000+ daily transactions.",
        fullDescription: `
            Engineered a mission-critical Order Management System utilizing cutting-edge microservices architecture capable of handling massive transaction volumes.
            The system demonstrates mastery of distributed systems, event-driven architecture, and enterprise-grade reliability patterns.
            
            Key Achievements:
            • Designed 8+ interconnected .NET 7 microservices following SOLID principles
            • Implemented event-driven architecture using Azure Service Bus for 99.99% message delivery guarantee
            • Engineered CQRS pattern separating read/write operations with 300% query performance improvement
            • Built comprehensive monitoring with Application Insights tracking 400+ KPIs
            • Achieved zero-downtime deployments with blue-green strategy
        `,
        highlights: [
            { icon: "⚙️", text: "8+ microservices handling 50,000+ daily transactions" },
            { icon: "📬", text: "Event-driven architecture with 99.99% delivery guarantee" },
            { icon: "🔄", text: "CQRS pattern implemented for 300% query improvement" },
            { icon: "📊", text: "Real-time monitoring of 400+ KPIs" }
        ],
        images: [OrderManagement],
        techStack: [
            { name: ".NET 7", category: "Backend" },
            { name: "C#", category: "Backend" },
            { name: "Microservices", category: "Backend" },
            { name: "CQRS Pattern", category: "Backend" },
            { name: "Entity Framework Core", category: "Backend" },
            { name: "SQL Server", category: "Database" },
            { name: "Azure Service Bus", category: "Cloud" },
            { name: "Application Insights", category: "Cloud" },
            { name: "Docker", category: "Cloud" },
            { name: "Azure Functions", category: "Cloud" }
        ],
        year: "2023-2024",
        duration: "12 months",
        team: "5-person engineering team",
        impact: "Reduced order processing time by 60% and increased system reliability to 99.99% uptime"
    },
    {
        id: 4,
        title: "Flex Leasing Platform",
        tagline: "Comprehensive automotive leasing workflow engine",
        category: "Full-Stack",
        badge: "FinTech",
        shortDescription: "Complete car leasing ecosystem featuring contract lifecycle management, payment processing, and compliance automation.",
        fullDescription: `
            Developed a comprehensive leasing platform that streamlines the entire vehicle leasing lifecycle from quote generation to contract management and payment reconciliation.
            The system integrates with external payment gateways, regulatory compliance engines, and accounting systems.
            
            Key Achievements:
            • Built end-to-end leasing workflows supporting 20+ contract types
            • Integrated payment gateway supporting multiple currencies and payment methods
            • Implemented regulatory compliance engine for multi-jurisdiction support
            • Engineered automated reconciliation reducing manual work by 85%
            • Delivered mobile-responsive UI serving 5,000+ concurrent users
        `,
        highlights: [
            { icon: "📋", text: "20+ contract types with automated workflow management" },
            { icon: "💳", text: "Multi-currency payment gateway integration" },
            { icon: "⚖️", text: "Regulatory compliance automation for multiple jurisdictions" },
            { icon: "📱", text: "Mobile-responsive design serving 5,000+ concurrent users" }
        ],
        images: [flexleasing],
        techStack: [
            { name: "Angular", category: "Frontend" },
            { name: "TypeScript", category: "Frontend" },
            { name: "Bootstrap", category: "Frontend" },
                        { name: "Leaflet", category: "Frontend" },

                                    { name: "OpenStreetMap", category: "Frontend" },

            { name: "RxJS", category: "Frontend" },
            { name: "ASP.NET Core", category: "Backend" },
            { name: "C#", category: "Backend" },
            { name: "Entity Framework Core", category: "Backend" },
            { name: "SQL Server", category: "Database" },
            { name: "Azure", category: "Cloud" },
            { name: "Payment APIs", category: "Integrations" }
        ],
        year: "2021-2023",
        duration: "18 months",
        team: "5-person development team",
        impact: "Reduced manual processing by 85% and supported 5,000+ concurrent users with 99.95% uptime"
    },
    {
        id: 5,
        title: "Starc Education Platform",
        tagline: "Interactive online learning and collaboration ecosystem",
        category: "Frontend",
        badge: "EdTech",
        shortDescription: "Modern e-learning platform enabling seamless collaboration between students and educators with real-time features.",
        fullDescription: `
            Contributed as Senior Frontend Developer to Starc, a transformative online education platform connecting students and teachers.
            Built responsive UI components, implemented real-time collaboration features, and optimized performance for scale.
            
            Key Achievements:
            • Architected reusable component library used across 50+ pages
            • Implemented real-time collaboration features using WebSockets
            • Optimized bundle size by 45% through code splitting and lazy loading
            • Built responsive UI components serving web and mobile platforms
            • Achieved 95+ Lighthouse performance score
        `,
        highlights: [
            { icon: "🎓", text: "Reusable component library for 50+ pages" },
            { icon: "⚡", text: "45% bundle size reduction through optimization" },
            { icon: "📱", text: "Responsive design supporting web and mobile" },
            { icon: "⭐", text: "95+ Lighthouse performance score" }
        ],
        images: [starc],
        techStack: [
            { name: "React", category: "Frontend" },
            { name: "TypeScript", category: "Frontend" },
            { name: "Redux", category: "Frontend" },
            { name: "Material-UI", category: "Frontend" },
            { name: "WebSockets", category: "Frontend" },
            { name: ".NET", category: "Backend" },
            { name: "Express", category: "Backend" },
            { name: "MongoDB", category: "Database" },
            { name: "Firebase", category: "Cloud" },
            { name: "WebRTC", category: "Integrations" }
        ],
        year: "2018-2020",
        duration: "24 months",
        team: "4-person frontend team",
        impact: "Served 50,000+ daily active users with 99.5% uptime and 4.8/5 user satisfaction"
    },
    {
        id: 6,
        title: "Mount Healing",
        tagline: "Organic wellness e-commerce ecosystem",
        category: "Full-Stack",
        badge: "E-Commerce",
        shortDescription: "Modern e-commerce platform promoting organic wellness products and traditional healing solutions from Gilgit-Baltistan.",
        fullDescription: `
            Designed and developed a premium e-commerce platform that celebrates the natural richness of Gilgit-Baltistan region.
            The platform combines modern technology with cultural authenticity, featuring intelligent product recommendations and seamless checkout.
            
            Key Achievements:
            • Built full-stack e-commerce solution with 500+ product listings
            • Implemented AI-powered product recommendation engine
            • Integrated Stripe for secure payment processing
            • Optimized for SEO achieving first-page Google rankings
            • Designed responsive UI with 98% mobile compatibility
            • Implemented inventory management and order fulfillment system
        `,
        highlights: [
            { icon: "🛍️", text: "Easy to manage inventory" },
            { icon: "🤖", text: "AI-powered product detail" },
            { icon: "📈", text: "Best Google rankings for key terms" }
        ],
        images: [mounthealing],
        techStack: [
            { name: "WordPress", category: "Frontend" },
            { name: "Hotinger", category: "Frontend" },
            { name: "hpanel", category: "Backend" },
            { name: "Hotinger hpanel", category: "Database" },
            { name: "Manual API", category: "Payments" },
            { name: "Hostinger", category: "Cloud" },
            { name: "SEO Optimization", category: "Tools" }
        ],
        year: "2026",
        duration: "3 months",
        team: "Freelance solo project",
        impact: "Generated $50K+ in revenue with 98% customer satisfaction and 60% repeat purchase rate"
    }
];
